'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '../../lib/supabase/client';
import {
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  RotateCcw,
  Volume2,
  VolumeX,
  Phone,
  PhoneIncoming,
  CheckCircle,
  XCircle,
  Wifi,
} from 'lucide-react';

interface VideoCallScreenProps {
  chatId: string;
  localUserId: string;
  remoteUserId: string;
  remoteUserName?: string;
  onEnd: () => void;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

type CallPhase = 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

export default function VideoCallScreen({
  chatId,
  localUserId,
  remoteUserId,
  remoteUserName = 'Contact',
  onEnd,
}: VideoCallScreenProps) {
  const supabase = createClient();

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const signalChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isCaller = localUserId < remoteUserId;

  const [phase, setPhase] = useState<CallPhase>('connecting');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const durationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const sendSignal = useCallback(
    async (signalType: string, payload: object) => {
      if (!sessionIdRef.current) return;
      await supabase.from('call_signals').insert({
        session_id: sessionIdRef.current,
        sender_id: localUserId,
        signal_type: signalType,
        payload,
      });
    },
    [localUserId, supabase]
  );

  const cleanup = useCallback(() => {
    if (durationRef.current) clearInterval(durationRef.current);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    signalChannelRef.current?.unsubscribe();
    pcRef.current = null;
    localStreamRef.current = null;
    signalChannelRef.current = null;
  }, []);

  const endCall = useCallback(async () => {
    if (sessionIdRef.current) {
      await supabase
        .from('call_sessions')
        .update({ call_status: 'ended', updated_at: new Date().toISOString() })
        .eq('id', sessionIdRef.current);
      await sendSignal('end', {});
    }
    cleanup();
    setPhase('ended');
    setTimeout(onEnd, 800);
  }, [cleanup, onEnd, sendSignal, supabase]);

  // ─── WebRTC setup ────────────────────────────────────────────────────────────

  const createPeerConnection = useCallback(
    (sessionId: string) => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      pcRef.current = pc;

      // Add local tracks
      localStreamRef.current?.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current!);
      });

      // Remote stream → video element
      pc.ontrack = (e) => {
        if (remoteVideoRef.current && e.streams[0]) {
          remoteVideoRef.current.srcObject = e.streams[0];
          setPhase('active');
          durationRef.current = setInterval(
            () => setCallDuration((d) => d + 1),
            1000
          );
        }
      };

      // ICE candidates → signaling channel
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          sendSignal('ice', { candidate: e.candidate.toJSON() });
        }
      };

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === 'disconnected' ||
          pc.connectionState === 'failed'
        ) {
          endCall();
        }
      };

      return pc;
    },
    [endCall, sendSignal]
  );

  // ─── Subscribe to incoming signals ──────────────────────────────────────────

  const subscribeToSignals = useCallback(
    (sessionId: string) => {
      const channel = supabase
        .channel(`call_signals:${sessionId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'call_signals',
            filter: `session_id=eq.${sessionId}`,
          },
          async (payload) => {
            const signal = payload.new as {
              sender_id: string;
              signal_type: string;
              payload: any;
            };

            // Ignore own signals
            if (signal.sender_id === localUserId) return;

            const pc = pcRef.current;
            if (!pc) return;

            if (signal.signal_type === 'answer') {
              await pc.setRemoteDescription(
                new RTCSessionDescription(signal.payload)
              );
            } else if (signal.signal_type === 'offer') {
              await pc.setRemoteDescription(
                new RTCSessionDescription(signal.payload)
              );
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await sendSignal('answer', answer);
            } else if (signal.signal_type === 'ice') {
              try {
                await pc.addIceCandidate(
                  new RTCIceCandidate(signal.payload.candidate)
                );
              } catch (_) {}
            } else if (signal.signal_type === 'end') {
              cleanup();
              setPhase('ended');
              setTimeout(onEnd, 800);
            }
          }
        )
        .subscribe();

      signalChannelRef.current = channel;
    },
    [cleanup, localUserId, onEnd, sendSignal, supabase]
  );

  // ─── Initiate call (caller) ──────────────────────────────────────────────────

  const startCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // Create session
      const { data: session, error } = await supabase
        .from('call_sessions')
        .insert({
          caller_id: localUserId,
          callee_id: remoteUserId,
          chat_id: chatId,
          call_status: 'pending',
        })
        .select()
        .single();

      if (error || !session) throw new Error('Failed to create call session');
      sessionIdRef.current = session.id;

      subscribeToSignals(session.id);
      const pc = createPeerConnection(session.id);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal('offer', offer);

      setPhase('ringing');
    } catch (err: any) {
      setErrorMsg(err.message || 'Could not access camera/microphone');
      setPhase('error');
    }
  }, [
    chatId,
    createPeerConnection,
    localUserId,
    remoteUserId,
    sendSignal,
    subscribeToSignals,
    supabase,
  ]);

  // ─── Receive call (callee) ───────────────────────────────────────────────────

  const receiveCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // Find pending session
      const { data: session } = await supabase
        .from('call_sessions')
        .select('*')
        .eq('chat_id', chatId)
        .eq('callee_id', localUserId)
        .eq('call_status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (session) {
        sessionIdRef.current = session.id;
        await supabase
          .from('call_sessions')
          .update({ call_status: 'active', updated_at: new Date().toISOString() })
          .eq('id', session.id);

        subscribeToSignals(session.id);
        createPeerConnection(session.id);
        setPhase('ringing');
      } else {
        // No existing session — wait for caller to create one
        const waitChannel = supabase
          .channel(`call_wait:${chatId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'call_sessions',
              filter: `chat_id=eq.${chatId}`,
            },
            async (payload) => {
              const newSession = payload.new as any;
              if (newSession.callee_id !== localUserId) return;
              sessionIdRef.current = newSession.id;
              await supabase
                .from('call_sessions')
                .update({
                  call_status: 'active',
                  updated_at: new Date().toISOString(),
                })
                .eq('id', newSession.id);
              subscribeToSignals(newSession.id);
              createPeerConnection(newSession.id);
              setPhase('ringing');
              waitChannel.unsubscribe();
            }
          )
          .subscribe();
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Could not access camera/microphone');
      setPhase('error');
    }
  }, [chatId, createPeerConnection, localUserId, subscribeToSignals, supabase]);

  // ─── Boot ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isCaller) {
      startCall();
    } else {
      receiveCall();
    }
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Controls ────────────────────────────────────────────────────────────────

  const toggleMic = () => {
    localStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setMicOn((v) => !v);
  };

  const toggleCam = () => {
    localStreamRef.current?.getVideoTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setCamOn((v) => !v);
  };

  const toggleSpeaker = () => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = speakerOn;
    }
    setSpeakerOn((v) => !v);
  };

  const flipCamera = async () => {
    const videoTrack = localStreamRef.current?.getVideoTracks()[0];
    if (!videoTrack) return;
    const settings = videoTrack.getSettings();
    const facingMode =
      settings.facingMode === 'user' ? 'environment' : 'user';
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      const newVideoTrack = newStream.getVideoTracks()[0];
      const sender = pcRef.current
        ?.getSenders()
        .find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newVideoTrack);
      videoTrack.stop();
      localStreamRef.current?.removeTrack(videoTrack);
      localStreamRef.current?.addTrack(newVideoTrack);
      if (localVideoRef.current)
        localVideoRef.current.srcObject = localStreamRef.current;
    } catch (_) {}
  };

  // ─── Duration format ─────────────────────────────────────────────────────────

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: '#0a1520' }}
    >
      {/* Remote video (full screen) */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ display: phase === 'active' ? 'block' : 'none' }}
      />

      {/* Connecting / ringing overlay */}
      {phase !== 'active' && phase !== 'ended' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
          {/* Animated ring pulse for ringing state */}
          <div className="relative flex items-center justify-center">
            {phase === 'ringing' && (
              <>
                <span className="absolute w-36 h-36 rounded-full animate-ping opacity-20"
                  style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }} />
                <span className="absolute w-28 h-28 rounded-full animate-ping opacity-30 animation-delay-150"
                  style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)', animationDelay: '0.3s' }} />
              </>
            )}
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-3xl relative z-10"
              style={{
                background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
              }}
            >
              {remoteUserName.charAt(0).toUpperCase()}
            </div>
          </div>

          <p className="text-white text-xl font-semibold">{remoteUserName}</p>

          {/* Call State Badge */}
          {phase === 'connecting' && (
            <CallStateBadge
              icon={<Wifi size={14} className="animate-pulse" />}
              label="Starting camera…"
              color="rgba(255,255,255,0.15)"
              textColor="text-white/70"
              pulse
            />
          )}
          {phase === 'ringing' && (
            <CallStateBadge
              icon={isCaller
                ? <Phone size={14} className="animate-bounce" />
                : <PhoneIncoming size={14} className="animate-bounce" />}
              label={isCaller ? 'Ringing…' : 'Incoming call…'}
              color="rgba(40, 127, 216, 0.3)"
              textColor="text-blue-300"
              pulse
            />
          )}
          {phase === 'error' && (
            <CallStateBadge
              icon={<XCircle size={14} />}
              label={errorMsg || 'Connection error'}
              color="rgba(229, 62, 62, 0.25)"
              textColor="text-red-400"
            />
          )}
        </div>
      )}

      {/* Ended overlay */}
      {phase === 'ended' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <div className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(229,62,62,0.2)', border: '2px solid rgba(229,62,62,0.4)' }}>
            <PhoneOff size={32} className="text-red-400" />
          </div>
          <p className="text-white text-lg font-semibold">Call Ended</p>
          <CallStateBadge
            icon={<XCircle size={14} />}
            label={`Duration: ${formatDuration(callDuration)}`}
            color="rgba(229,62,62,0.2)"
            textColor="text-red-300"
          />
        </div>
      )}

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-5 pt-12 pb-4">
        <div className="flex flex-col gap-1">
          <p className="text-white font-semibold text-lg leading-tight">
            {remoteUserName}
          </p>
          {/* Inline call state indicator in top bar */}
          {phase === 'active' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <p className="text-green-400 text-sm font-medium">{formatDuration(callDuration)}</p>
            </div>
          )}
          {phase === 'ringing' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
              <p className="text-blue-300 text-sm">{isCaller ? 'Ringing…' : 'Incoming…'}</p>
            </div>
          )}
          {phase === 'connecting' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <p className="text-yellow-300 text-sm">Connecting…</p>
            </div>
          )}
        </div>

        {/* Active call badge top-right */}
        {phase === 'active' && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}>
            <CheckCircle size={13} className="text-green-400" />
            <span className="text-green-400 text-xs font-semibold">Connected</span>
          </div>
        )}
      </div>

      {/* Local video (PiP) */}
      <div className="absolute top-20 right-4 z-20 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        style={{ width: 100, height: 140 }}>
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        {!camOn && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <VideoOff size={20} className="text-white/50" />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-12 pt-6"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
        }}>

        {/* Call state label above controls */}
        <div className="flex justify-center mb-4">
          {phase === 'ringing' && (
            <CallStateBadge
              icon={isCaller ? <Phone size={13} /> : <PhoneIncoming size={13} />}
              label={isCaller ? 'Ringing…' : 'Incoming call…'}
              color="rgba(40,127,216,0.25)"
              textColor="text-blue-300"
              pulse
            />
          )}
          {phase === 'active' && (
            <CallStateBadge
              icon={<CheckCircle size={13} />}
              label={`Connected · ${formatDuration(callDuration)}`}
              color="rgba(52,211,153,0.15)"
              textColor="text-green-400"
            />
          )}
          {phase === 'connecting' && (
            <CallStateBadge
              icon={<Wifi size={13} />}
              label="Connecting…"
              color="rgba(255,255,255,0.1)"
              textColor="text-white/60"
              pulse
            />
          )}
        </div>

        <div className="flex items-center justify-center gap-5">
          {/* Speaker */}
          <ControlButton
            icon={speakerOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
            label={speakerOn ? 'Mute speaker' : 'Unmute speaker'}
            active={speakerOn}
            onClick={toggleSpeaker}
          />

          {/* Mic */}
          <ControlButton
            icon={micOn ? <Mic size={22} /> : <MicOff size={22} />}
            label={micOn ? 'Mute mic' : 'Unmute mic'}
            active={micOn}
            onClick={toggleMic}
          />

          {/* End call — prominent */}
          <div className="flex flex-col items-center gap-1.5">
            <button
              onClick={endCall}
              aria-label="End call"
              className="w-18 h-18 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90 shadow-lg"
              style={{
                width: 68,
                height: 68,
                background: 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)',
                boxShadow: '0 0 0 4px rgba(229,62,62,0.25), 0 4px 20px rgba(229,62,62,0.4)',
              }}
            >
              <PhoneOff size={28} className="text-white" />
            </button>
            <span className="text-red-400 text-xs font-semibold tracking-wide">End Call</span>
          </div>

          {/* Camera */}
          <ControlButton
            icon={camOn ? <Video size={22} /> : <VideoOff size={22} />}
            label={camOn ? 'Turn off camera' : 'Turn on camera'}
            active={camOn}
            onClick={toggleCam}
          />

          {/* Flip camera */}
          <ControlButton
            icon={<RotateCcw size={22} />}
            label="Flip camera"
            active
            onClick={flipCamera}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Call State Badge ────────────────────────────────────────────────────────

function CallStateBadge({
  icon,
  label,
  color,
  textColor,
  pulse = false,
}: {
  icon: React.ReactNode;
  label: string;
  color: string;
  textColor: string;
  pulse?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-full ${pulse ? 'animate-pulse' : ''}`}
      style={{ background: color, border: `1px solid ${color.replace('0.', '0.4').replace('0.15', '0.4').replace('0.25', '0.4').replace('0.3', '0.5')}` }}
    >
      <span className={textColor}>{icon}</span>
      <span className={`text-sm font-medium ${textColor}`}>{label}</span>
    </div>
  );
}

function ControlButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
      style={{
        background: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
        border: active ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <span className={active ? 'text-white' : 'text-white/40'}>{icon}</span>
    </button>
  );
}
